/**
 * The book's CONTACTS page offline: the people the story introduces. Where each one is comes from the
 * layout (POI id first, then a POI of the right kind) or, for the fence, from the weapons service, so the
 * page keeps working if the borough is rearranged.
 */
import type { GameContext } from '../core/context';
import type { PoiKind } from '../world/types';
import type { IconId } from '../ui/mapIcons';

export interface Contact {
  id: string;
  name: string;
  /** How a waypoint calls them ("Mags"). */
  short: string;
  role: string;
  where: string;
  about: string;
  poi: string;
  kind?: PoiKind;
  fence?: boolean;
  /** Their place's map icon (src/ui/mapIcons.ts), shown by their name in the book. */
  icon: IconId;
  /** Only listed once they have written or have work on the board (the side-activity people). */
  introduced?: boolean;
}

export const CONTACTS: readonly Contact[] = [
  {
    id: 'mags',
    icon: 'pint',
    name: 'Mags Delaney',
    short: 'Mags',
    role: 'The firm',
    where: 'The Crooked Anchor',
    about: 'Runs the firm from the Crooked Anchor’s back room. The story jobs are hers — knock on her door, or pick up when a phone box rings for you.',
    poi: 'mission-start-anchor',
    kind: 'missionStart',
  },
  {
    id: 'halloran',
    icon: 'key',
    name: 'Des Halloran',
    short: 'Halloran',
    role: 'Export orders',
    where: 'Halloran Motors',
    about: 'Buys motors for a boat that leaves on the tide, no questions asked. Once Mags vouches for you (after your first job) he always has an order going.',
    poi: 'garage-halloran',
    kind: 'garage',
  },
  {
    id: 'sid',
    icon: 'gun',
    name: 'Sid Marlowe',
    short: 'Sid',
    role: 'The fence — weapons',
    where: 'Gorringe’s scrap yard',
    about: 'Sells what you can’t buy in a shop: a cosh, a flick knife, a .38, a sawn-off and the odd box of rounds, cash only. Guns bring armed police.',
    poi: 'scrapyard-gorringe',
    fence: true,
  },
  {
    id: 'lenny',
    icon: 'phonebox',
    name: 'Lenny the Ear',
    short: 'Lenny',
    role: 'Street jobs',
    where: 'Phone Box, Coalgate',
    about: 'Hears everything and rings the box on Coalgate with street work: courier runs, a quiet word, wheelman jobs. He also pays cash for any matchbook with a number scrawled inside.',
    poi: 'phone-coalgate',
    introduced: true,
  },
  {
    id: 'rita',
    icon: 'flag',
    name: 'Rita Vance',
    short: 'Rita',
    role: 'Street races',
    where: 'Basin Crane Quay',
    about: 'Runs the night races from the crane quay: three courses, a stake in, a pot out, and rivals who don’t move over. Beat her times and she’ll have better ones for you.',
    poi: 'docks-crane-quay',
    introduced: true,
  },
  {
    id: 'dot',
    icon: 'cab',
    name: 'Dot Hennessy',
    short: 'Dot',
    role: 'Kestrel Cars',
    where: 'Phone Box, Bell Street',
    about: 'Dispatch at Kestrel Cars. Ring from the box on Bell Street for a minicab shift: the meter is yours, quick and careful rides earn tips, and a good take brings longer shifts.',
    poi: 'phone-tanners',
    kind: 'phonebox',
    introduced: true,
  },
  {
    id: 'pike',
    icon: 'spray',
    name: 'Pike’s Respray',
    short: 'Pike’s',
    role: 'Respray shop',
    where: 'Pike’s Respray',
    about: 'Drive a hot motor in and stop: new paint, dents knocked out, and the law no longer knows it. Dearer the hotter you are.',
    poi: 'respray-pike',
    kind: 'resprayShop',
  },
];

/**
 * Faces: people the story puts in your way who are not there to be visited — the Vanes and their minder,
 * the law, the firm's other hands. Each goes in CONTACTS (under its own heading, with its portrait) once
 * the job that introduces them is done, so the page fills up as the story goes on.
 */
export interface Face {
  id: string;
  name: string;
  role: string;
  about: string;
  /** Listed once this story job is done (mission id). */
  after: string;
}

export const FACES: readonly Face[] = [
  {
    id: 'ronnie',
    name: 'Ronnie Vane',
    role: 'The Vanes',
    about: 'Runs the docks and The Velvet Lamp with his brother and thinks Low Borough is his. Loud suits, short fuse, long memory — and you had his new Vesper away from the kerb.',
    after: 'borrowed-time',
  },
  {
    id: 'terry',
    name: 'Big Terry',
    role: 'Ronnie’s minder',
    about: 'Ronnie Vane’s minder. Loud. When Big Terry starts shouting, a patrol is never far behind.',
    after: 'borrowed-time',
  },
  {
    id: 'clive',
    name: 'Clive Vane',
    role: 'The quiet brother',
    about: 'The Vane who keeps the books — all of them. Bought Ronnie’s Vesper back off Mags without a word, which tells you who the dangerous one is.',
    after: 'borrowed-time',
  },
  {
    id: 'wilf',
    name: 'Wilf Tuckett',
    role: 'Arch 14, Cinder Yards',
    about: 'Fences out of the railway arches in Cinder Yards. Jumpy, and with reason: the last parcel he handed over came with a jam sandwich at the end of the yard.',
    after: 'wet-paper',
  },
  {
    id: 'holloway',
    name: 'DS Holloway',
    role: 'Low Borough CID',
    about: 'Wants you inside. When Holloway takes the radio it isn’t a patrol having a look any more — get out of sight and stay there.',
    after: 'gullet-job',
  },
  {
    id: 'pinch',
    name: 'Verger Pinch',
    role: 'St. Brannoc’s',
    about: 'Keeps a book in the vestry for Mags — “the Lord’s money as much as Mags’”, he says, and counts it twice.',
    after: 'the-collection',
  },
  {
    id: 'nobby',
    name: 'Nobby Clegg',
    role: 'Lockup 7, Pilcher Ginnel',
    about: 'Runs Mags’ slate out of Lockup 7. Pays out with a grin and collects without one.',
    after: 'the-collection',
  },
];

export function contactPos(ctx: GameContext, c: Contact): { x: number; z: number } | null {
  if (c.fence) {
    const f = ctx.services.tryGet('weapons')?.fence;
    if (f) return { x: f.pos.x, z: f.pos.z };
  }
  const pois = ctx.services.tryGet('layout')?.pois ?? [];
  const p = pois.find((q) => q.id === c.poi) ?? (c.kind ? pois.find((q) => q.kind === c.kind) : undefined);
  return p ? { x: p.pos.x, z: p.pos.z } : null;
}
